// Scored Fatigue dashboard: page chrome (title, screen nav, drill-scope switcher,
// breadcrumb) plus the active screen. Ports the prototype's sidebar/topbar to a
// horizontal nav so it sits inside the existing admin layout.

import React from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Activity,
  ChevronRight,
  CircleCheck,
  Clock3,
  Gauge,
  LayoutGrid,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Truck,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { qk } from '../../../../../lib/queryKeys';
import { useClientScope } from '../../../../../shared/contexts/ClientScopeContext';
import { fatigueApi } from '../data/fatigueApi';
import { DEMO_MODE, GROUPS, REGIONS, WORKERS } from './engine/data';
import { loadLiveRoster } from './engine/live';
import { DashProvider, useDash } from './state';
import type { DrillScope, ScreenKey } from './state';
import { OverviewScreen } from './screens/OverviewScreen';
import { PastScreen } from './screens/PastScreen';
import { TodayScreen } from './screens/TodayScreen';
import { ForwardScreen } from './screens/ForwardScreen';
import { ShiftsScreen } from './screens/ShiftsScreen';
import { DrivingScreen } from './screens/DrivingScreen';
import { ResilienceScreen } from './screens/ResilienceScreen';
import { ComplianceScreen } from './screens/ComplianceScreen';
import { RecommendationsScreen } from './screens/RecommendationsScreen';
import { WorkerScreen } from './screens/WorkerScreen';
import '../../../../../styles/fatigue-shadcn.css';

const NAV: { key: ScreenKey; label: string; icon: React.ElementType }[] = [
  { key: 'overview', label: 'Overview', icon: LayoutGrid },
  { key: 'past', label: 'Past', icon: Clock3 },
  { key: 'today', label: 'Today', icon: Gauge },
  { key: 'forward', label: 'Forward', icon: TrendingUp },
  { key: 'shifts', label: 'Shifts & Sleep', icon: Activity },
  { key: 'driving', label: 'Driving', icon: Truck },
  { key: 'resilience', label: 'OT Resilience', icon: Activity },
  { key: 'compliance', label: 'HSE Compliance', icon: ShieldCheck },
  { key: 'recommendations', label: 'AI Recommendations', icon: Sparkles },
];

const SCOPES: { key: DrillScope; label: string }[] = [
  { key: 'company', label: 'Company' },
  { key: 'region', label: 'Region' },
  { key: 'group', label: 'Group' },
  { key: 'individual', label: 'Individual' },
];

const NeedsRota: React.FC<{ title: string; needs: string }> = ({ title, needs }) => (
  <div className='rounded-md border border-dashed border-border px-6 py-14 text-center'>
    <div className='text-sm font-semibold'>{title}</div>
    <p className='mx-auto mt-1.5 max-w-md text-sm text-muted-foreground'>{needs}</p>
  </div>
);

const rotaGated = (title: string, Screen: React.FC): React.FC =>
  function Gated() {
    return DEMO_MODE.live ? (
      <NeedsRota
        title={`${title}: no rota data connected`}
        needs='This view is built from shift times, hours, overtime and driving records, which come from the client rota. Connect a rota source to enable it.'
      />
    ) : (
      <Screen />
    );
  };

const SCREENS: Record<ScreenKey, React.FC> = {
  overview: OverviewScreen,
  past: PastScreen,
  today: TodayScreen,
  forward: ForwardScreen,
  shifts: rotaGated('Shifts & Sleep', ShiftsScreen),
  driving: rotaGated('Driving', DrivingScreen),
  resilience: rotaGated('OT Resilience', ResilienceScreen),
  compliance: rotaGated('HSE Compliance', ComplianceScreen),
  recommendations: RecommendationsScreen,
  worker: WorkerScreen,
};

const Breadcrumb: React.FC = () => {
  const d = useDash();
  const crumbs: { label: string; onClick?: () => void }[] = [{ label: 'Company', onClick: d.resetToCompany }];
  if (d.activeRegion) {
    crumbs.push({ label: REGIONS[d.activeRegion].name, onClick: () => d.setDrillScope('region') });
  }
  if (d.activeGroup !== null) {
    crumbs.push({ label: GROUPS[d.activeGroup].name, onClick: () => d.setDrillScope('group') });
  }
  if (d.activeWorker && (d.drillScope === 'individual' || d.screen === 'worker')) {
    const name = WORKERS[d.activeWorker].name;
    crumbs.push({ label: name, onClick: d.screen === 'worker' ? undefined : () => d.go('worker') });
  }
  return (
    <nav className='flex flex-wrap items-center gap-1.5 text-[13px] text-muted-foreground' aria-label='Scope'>
      {crumbs.map((c, i) => (
        <React.Fragment key={`${c.label}-${i}`}>
          {i > 0 && <ChevronRight className='h-3.5 w-3.5' />}
          {c.onClick ? (
            <button type='button' onClick={c.onClick} className='hover:text-foreground hover:underline'>
              {c.label}
            </button>
          ) : (
            <span className='text-foreground'>{c.label}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};

const DashboardBody: React.FC<{ asOf: string | null }> = ({ asOf }) => {
  const d = useDash();
  const Screen = SCREENS[d.screen];
  const all = Object.values(WORKERS);
  const total = all.length;
  const warming = all.filter((w) => w.coverage?.baselineWarming).length;
  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-wrap items-start justify-between gap-4'>
        <div className='min-w-0'>
          <h1 className='text-2xl font-semibold tracking-tight'>Fatigue Management</h1>
          <p className='mt-1 text-sm text-muted-foreground'>
            Fatigue exposure, circadian disruption and load readiness.
          </p>
        </div>
        <div className='flex gap-0.5 rounded-md bg-muted p-0.5' role='tablist' aria-label='Drill scope'>
          {SCOPES.map((s) => (
            <button
              key={s.key}
              type='button'
              role='tab'
              aria-selected={d.drillScope === s.key}
              onClick={() => d.setDrillScope(s.key)}
              className={cn(
                'rounded-[5px] px-3 py-1 text-xs font-medium transition-colors',
                d.drillScope === s.key
                  ? 'bg-card text-foreground shadow-[0_0_0_1px_var(--color-border)]'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className='-mx-1 overflow-x-auto px-1'>
        <div className='flex min-w-max gap-1 border-b border-border'>
          {NAV.map(({ key, label, icon: Icon }) => {
            const active = d.screen === key || (d.screen === 'worker' && key === 'overview');
            return (
              <button
                key={key}
                type='button'
                onClick={() => d.go(key)}
                className={cn(
                  '-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-[13px] font-medium transition-colors',
                  active
                    ? 'border-foreground text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className='h-3.5 w-3.5 opacity-70' />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className='flex items-center justify-between gap-3'>
        <Breadcrumb />
        {asOf ? (
          <span className='text-xs text-muted-foreground'>
            Live wearable data · latest reading {asOf} · load scores need rota data
            {warming > 0 && ` · ${warming} of ${total} on a short baseline (<14 days)`}
          </span>
        ) : (
          <span className='inline-flex items-center gap-1 rounded-[4px] bg-warning/10 px-1.5 py-0.5 text-[11px] font-medium text-warning'>
            <CircleCheck className='h-3 w-3' /> Demo data
          </span>
        )}
      </div>

      <Screen />
    </div>
  );
};

const Message: React.FC<{ title: string; children?: React.ReactNode }> = ({ title, children }) => (
  <div className='rounded-md border border-border px-6 py-14 text-center'>
    <div className='text-sm font-semibold'>{title}</div>
    {children && <p className='mx-auto mt-1.5 max-w-md text-sm text-muted-foreground'>{children}</p>}
  </div>
);

const LiveDashboard: React.FC<{ clientId: string }> = ({ clientId }) => {
  const q = useQuery({
    queryKey: qk.fatigue.workforce(clientId),
    queryFn: () => fatigueApi.getWorkforce(clientId),
    staleTime: 5 * 60_000,
  });
  // Swap the engine roster once per payload, before any screen reads it.
  const asOf = React.useMemo(() => {
    if (!q.data?.workers.length) return null;
    loadLiveRoster(q.data);
    return q.data.asOf;
  }, [q.data]);

  if (q.isLoading) return <Message title='Loading fatigue data…' />;
  if (q.isError) {
    const status = (q.error as { response?: { status?: number; data?: { message?: string } } })?.response;
    return (
      <Message title='Fatigue data is not available'>
        {status?.status === 403 ? status.data?.message : 'The request failed. Try again shortly.'}
      </Message>
    );
  }
  if (!q.data?.workers.length) {
    return (
      <Message title='No scorable wearable data'>
        None of the {q.data?.enrolled ?? 0} people in this client have enough HRV, resting-HR and sleep history yet.
      </Message>
    );
  }
  return (
    <DashProvider key={`${clientId}-${asOf}`}>
      <DashboardBody asOf={asOf} />
    </DashProvider>
  );
};

export const FatigueDashboard: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { effectiveClientId } = useClientScope();
  return (
    <div className={cn('fatigue-page p-1', isDark && 'dark')}>
      {effectiveClientId ? (
        <LiveDashboard clientId={effectiveClientId} />
      ) : (
        <Message title='Select a client'>
          The scored fatigue dashboard reads one client at a time. Choose a client in the client selector.
        </Message>
      )}
    </div>
  );
};
